/**
 * LandlordTypeSelection Component
 * Allows landlords to choose between "Particulier" (private) or "Agency"
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getPostLoginRoute } from '../utils/routing';
import { 
  User, 
  Building2, 
  ArrowRight, 
  ArrowLeft,
  Home,
  Users,
  Shield,
  CheckCircle,
  Sparkles,
  Briefcase,
  FileText,
  Calendar,
  Rocket
} from 'lucide-react';
import StudyStayLogo from '../components/StudyStayLogo';
import toast from 'react-hot-toast';

export default function LandlordTypeSelection() {
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  const { user, refreshUserType, loading: authLoading, userType, landlordType } = useAuth();
  const navigate = useNavigate();

  // CRITICAL: If landlordType already exists, redirect immediately
  // This prevents showing this screen if landlordType was already set during registration
  useEffect(() => {
    const checkExistingLandlordType = async () => {
      if (!user || userType !== 'landlord') {
        return;
      }

      // ALWAYS check database first (most reliable)
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.landlordType) {
            console.log('🏠 LandlordTypeSelection: landlordType found in database:', userData.landlordType);
            const route = getPostLoginRoute(userData, user.email);
            console.log('🏠 LandlordTypeSelection: Redirecting immediately to:', route);
            navigate(route, { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Error checking landlordType:', error);
      }

      // Also check AuthContext (as backup)
      if (landlordType) {
        console.log('🏠 LandlordTypeSelection: landlordType exists in context:', landlordType);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const route = getPostLoginRoute(userDoc.data(), user.email);
            console.log('🏠 LandlordTypeSelection: Redirecting to:', route);
            navigate(route, { replace: true });
            return;
          }
        } catch (error) {
          console.error('Error getting user doc:', error);
        }
      }
      
      // Only show the selection screen if landlordType truly doesn't exist
      console.log('🏠 LandlordTypeSelection: No landlordType found, showing selection screen');
      console.log('🏠 LandlordTypeSelection: user', user?.uid);
      console.log('🏠 LandlordTypeSelection: userType', userType);
      console.log('🏠 LandlordTypeSelection: landlordType', landlordType);
      setIsVisible(true);
    };
    
    checkExistingLandlordType();
  }, [user, userType, landlordType, navigate]);

  // Show loading if user is not loaded yet
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ios-blue via-blue-600 to-purple-700 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg animate-bounce">
            <StudyStayLogo className="w-10 h-10" />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="text-white mt-4">Loading your account...</p>
        </div>
      </div>
    );
  }

  // Redirect if no user
  if (!user) {
    console.log('No user found, redirecting to login');
    navigate('/login');
    return null;
  }

  // Show loading while checking if landlordType exists
  // The useEffect above will redirect if landlordType exists
  if (!isVisible) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ios-blue via-blue-600 to-purple-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="text-white mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error('Please select your landlord type');
      return;
    }

    if (!user) {
      toast.error('User not loaded. Please try again.');
      return;
    }

    try {
      setLoading(true);
      
      // Update user document in Firestore
      const userRef = doc(db, 'users', user.uid);
      
      // Update with landlordType
      await setDoc(userRef, {
        role: 'landlord',
        userType: 'landlord', // Keep for backward compatibility
        landlordType: selectedType, // 'private' or 'agency'
        displayName: user.displayName || '',
        email: user.email || '',
        updatedAt: new Date()
      }, { merge: true });

      toast.success('Account type set successfully!');
      
      // Wait a moment for Firestore to update, then use central routing
      // Don't wait for refreshUserType() as it might be slow - read directly from DB
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for Firestore
      
      // Read directly from database (most up-to-date) and redirect
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const route = getPostLoginRoute(userData, user.email);
        console.log('LandlordTypeSelection: Using central routing, redirecting to:', route);
        
        // Refresh context in background (don't wait)
        refreshUserType().catch(err => console.error('Error refreshing user type:', err));
        
        // Navigate immediately
        navigate(route, { replace: true });
      } else {
        // Fallback routing
        if (selectedType === 'agency') {
          navigate('/landlord/agency/dashboard', { replace: true });
        } else {
          navigate('/landlord', { replace: true });
        }
      }
    } catch (error) {
      console.error('Error setting landlord type:', error);
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please check your Firestore rules.');
      } else if (error.code === 'not-found') {
        toast.error('User document not found. Please try logging in again.');
      } else {
        toast.error(`Failed to set account type: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-ios-blue via-blue-600 to-purple-700 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white opacity-5 rounded-full animate-bounce"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white opacity-5 rounded-full animate-spin"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white opacity-20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl w-full">
          {/* Header */}
          <div className={`text-center mb-12 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <div className="relative inline-block">
              <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl animate-bounce-gentle">
                <StudyStayLogo className="w-16 h-16" />
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 w-24 h-24 bg-white rounded-3xl mx-auto blur-xl opacity-30 animate-pulse"></div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Wat voor verhuurder ben je?
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Kies het type account dat het beste bij je past
            </p>
          </div>

          {/* Landlord Type Selection Cards */}
          <div className={`grid md:grid-cols-2 gap-8 mb-12 transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            {/* Private Landlord Card */}
            <div
              className={`relative group cursor-pointer transform transition-all duration-500 ${
                selectedType === 'private' 
                  ? 'scale-105 shadow-2xl' 
                  : 'hover:scale-102 hover:shadow-xl'
              }`}
              onClick={() => setSelectedType('private')}
            >
              <div className={`bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-8 border-2 transition-all duration-300 ${
                selectedType === 'private' 
                  ? 'border-white shadow-2xl' 
                  : 'border-white border-opacity-30 hover:border-opacity-60'
              }`}>
                {/* Selection indicator */}
                {selectedType === 'private' && (
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                )}

                {/* Icon */}
                <div className="text-center mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                    selectedType === 'private' ? 'animate-pulse' : ''
                  }`}>
                    <User className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Particulier</h3>
                  <p className="text-blue-100">Ik verhuur zelf mijn pand(en)</p>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-white">
                    <Home className="w-5 h-5 text-blue-300" />
                    <span>Beheer je eigen panden</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white">
                    <Users className="w-5 h-5 text-green-300" />
                    <span>Direct contact met studenten</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white">
                    <FileText className="w-5 h-5 text-purple-300" />
                    <span>Volledige controle over contracten</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white">
                    <Calendar className="w-5 h-5 text-orange-300" />
                    <span>Plan je eigen bezichtigingen</span>
                  </div>
                </div>

                {/* Benefits */}
                <div className="mt-6 pt-6 border-t border-white border-opacity-20">
                  <div className="flex items-center justify-center space-x-4 text-sm text-blue-100">
                    <div className="flex items-center space-x-1">
                      <Shield className="w-4 h-4" />
                      <span>100% Controle</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Sparkles className="w-4 h-4" />
                      <span>Optioneel: Agency hulp</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agency Card — Coming Soon */}
            <div
              className="relative group transform transition-all duration-500 opacity-50 cursor-not-allowed"
              onClick={() => toast('Agency accounts komen binnenkort beschikbaar!', { icon: '🚀' })}
            >
              <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                Coming Soon
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-8 border-2 border-white border-opacity-20">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white/60 mb-2">Agency / Makelaar</h3>
                  <p className="text-blue-200/60">Ik beheer panden voor anderen</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3 text-white/50">
                    <Building2 className="w-5 h-5" />
                    <span>Beheer meerdere panden</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/50">
                    <Briefcase className="w-5 h-5" />
                    <span>Professioneel account</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/50">
                    <Users className="w-5 h-5" />
                    <span>Help particuliere verhuurders</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/50">
                    <Rocket className="w-5 h-5" />
                    <span>Smart filtering & suggesties</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white border-opacity-10">
                  <p className="text-center text-sm text-blue-200/60">Binnenkort beschikbaar</p>
                </div>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className={`text-center transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
            <form onSubmit={handleSubmit}>
              <button
                type="submit"
                disabled={loading || !selectedType}
                className={`group inline-flex items-center justify-center px-12 py-6 rounded-2xl font-bold text-xl transition-all duration-300 transform ${
                  selectedType 
                    ? 'bg-white text-ios-blue shadow-2xl hover:bg-gray-50 hover:scale-105' 
                    : 'bg-white bg-opacity-20 text-white cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ios-blue mr-3"></div>
                    <span>Setting up your account...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6 mr-3 animate-spin" />
                    <span>Doorgaan</span>
                    <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
            
            <p className="text-blue-100 text-lg mt-6">
              <button
                onClick={() => navigate('/user-type-login')}
                className="hover:text-white font-medium transition-colors duration-200 flex items-center space-x-2 mx-auto"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Terug</span>
              </button>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
}

